import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Badge, Button, Card, CardBody, Col, Input, Row } from "reactstrap";
import Select, { components } from "react-select";
import { getTraineeCourses } from "action/trainee";
import { getCourseCategoryList } from "action/courseCategory";
import { traineeCourses } from "constants/trainee";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import { DEFAULT_RECORDS_PER_PAGE, BATCH_STATUS_OPTIONS, STATUS_BADGE_COLORS } from "components/constant";
import { colors } from "variables/charts";
import {
  COURSE_LEVELS,
  COURSE_TYPES,
  COURSE_MODE,
} from "components/constant";
import {
  formatDate,
  toTitleCase,
} from "components/CommonComponents/CommonFunctions";
import AttendanceRightPanel from "components/CommonComponents/AttendanceRightPanel";
import {urlEncodedId} from "components/CommonComponents/encodeDecodeFunction";

function TraineeCourses({ traineeID }) {
  const dispatch = useDispatch();
  const [pageNum, setPageNum] = useState(1);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [filterState, setFilterState] = useState({
    searchName: "",
    searchStatus: null,
    searchCategory: null,
    searchLevel: null,
    searchType: null,
    searchMode: null,
    searchDate: [null, null],
    filterParams: [],
  });

  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const traineeCoursesData = useSelector(
    (state) => state.trainees.traineeCourses,
  );
  const courseCategoryListData = useSelector(
    (state) => state.courseCategory.courseCategoryList,
  );

  useEffect(() => {
    getCourseCategoryList(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (traineeID) {
      getTraineeCourses(dispatch, traineeID, pageNum, filterState.filterParams);
    }
    return () => {
      dispatch({ type: traineeCourses, payload: {} });
    };
  }, [dispatch, pageNum, filterState.filterParams, traineeID]);

  useEffect(() => {
    if (traineeCoursesData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: traineeCoursesData?.data?.courses?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (traineeCoursesData?.code || traineeCoursesData?.message) {
      setListState({
        isLoading: false,
        error: traineeCoursesData?.message || "Failed to load courses!",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [traineeCoursesData]);

  const courseCategoryOptions =
    courseCategoryListData?.data?.courseCategories
      ?.filter((category) => category.status === "ACTIVE")
      .map((category) => ({
        value: category.name,
        label: category.name,
      })) || [];

  const buildFilterParams = () => {
    const filterParams = [];

    if (filterState.searchName?.trim()) {
      filterParams.push({
        field: "CourseName",
        condition: "like",
        filterValues: [filterState.searchName.trim()],
      });
    }
    if (filterState.searchCategory?.value) {
      filterParams.push({
        field: "Category",
        condition: "eq",
        filterValues: [filterState.searchCategory.value],
      });
    }
    if (filterState.searchLevel?.value) {
      filterParams.push({
        field: "Level",
        condition: "eq",
        filterValues: [filterState.searchLevel.value],
      });
    }
    if (filterState.searchType?.value) {
      filterParams.push({
        field: "Type",
        condition: "eq",
        filterValues: [filterState.searchType.value],
      });
    }
    if (filterState.searchMode?.value) {
      filterParams.push({
        field: "Mode",
        condition: "eq",
        filterValues: [filterState.searchMode.value],
      });
    }
    if (filterState.searchStatus?.value) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filterState.searchStatus.value],
      });
    }

    if (filterState.searchDate && filterState.searchDate[0]) {
      if (filterState.searchDate[1]) {
        filterParams.push({
          field: "EnrolledOnDate",
          condition: "btw",
          filterValues: [
            formatDate(filterState.searchDate[0]),
            formatDate(filterState.searchDate[1]),
          ],
        });
      } else {
        filterParams.push({
          field: "EnrolledOnDate",
          condition: "eq",
          filterValues: [formatDate(filterState.searchDate[0])],
        });
      }
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));
      setFilterState((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));

      if (pageNum !== 1) {
        setPageNum(1);
      }
    }
  };

  const clearSearch = () => {
    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    setFilterState({
      searchName: "",
      searchCategory: null,
      searchLevel: null,
      searchType: null,
      searchMode: null,
      searchStatus: null,
      searchDate: [null, null],
      filterParams: [],
    });

    if (pageNum !== 1) setPageNum(1);
  };



  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={STATUS_BADGE_COLORS[props.data.value] || "secondary"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={STATUS_BADGE_COLORS[props.data.value] || "secondary"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const tableData = traineeCoursesData?.data?.courses?.map((item, index) => {
    return {
      ...item,
      id: item.courseID,
      srNo:
        (pageNum - 1) *
          (traineeCoursesData?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
      courseName: item?.courseName || "-",
      category: item?.category || "-",
      level: item?.level || "-",
      type: item?.batch?.type || "-",
      contentDeliveryMode: item?.batch?.mode || "-",
      enrolledOn: item?.batch?.enrolledOnDate || "-",
      status: item?.batch?.status || "-",
    };
  });

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      centerAlign: true,
      minWidth: 70,
      maxWidth: 70,
    },
    {
      Header: "Course Name",
      accessor: "courseName",
      minWidth: 180,
      maxWidth: 200,
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },
    {
      Header: "Category",
      accessor: "category",
      minWidth: 140,
      maxWidth: 160,
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },
    {
      Header: "Level",
      accessor: "level",
      minWidth: 140,
      maxWidth: 180,
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },
    {
      Header: "Type",
      accessor: "type",
      minWidth: 120,
      maxWidth: 160,
      Cell: ({ value }) => (
        <div className="text-wrapper">{toTitleCase(value)}</div>
      ),
    },
    {
      Header: "Mode",
      accessor: "contentDeliveryMode",
      minWidth: 130,
      maxWidth: 160,
      Cell: ({ value }) => (
        <div className="text-wrapper">{toTitleCase(value)}</div>
      ),
    },
    {
      Header: "Enrolled On",
      accessor: "enrolledOn",
      minWidth: 120,
      maxWidth: 140,
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },
    {
      Header: "Status",
      accessor: "status",
      minWidth: 90,
      maxWidth: 100,
      centerAlign: true,
      Cell: ({ value }) => {
        const statusLabel =
          BATCH_STATUS_OPTIONS.find((option) => option.value === value)
            ?.label || value;

        return (
          <Badge color={STATUS_BADGE_COLORS[value] || "secondary"} pill>
            {statusLabel}
          </Badge>
        );
      },
    },

    {
      Header: "Learning Status",
      accessor: "courseProgress",
      minWidth: 120,
      maxWidth: 140,
      Cell: ({ value, row }) => {
        const courseType = row.original?.batch?.type;

        // LIVE course → Attendance icon
        if (courseType === "LIVE") {
          return (
            <div className="text-center">
              <Button
                color="link"
                className="p-0"
                title="View Attendance"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedCourse(row.original);
                  setIsRightPanelOpen(true);
                }}
              >
                <i className="fa fa-calendar-alt text-info" />
                   </Button>
                <div className="text-muted  ">View Attendance</div>
            </div>
          );
        }

        // RECORDED course → Learning Status
        if (courseType === "RECORDED") {
          const totalLessons = value?.totalLessons || 0;
          const completedLessons = value?.completedLessons || 0;
         const progressPercentage = value?.lessonProgressPercent || 0;
          return (
            <div className="course-progress">
              <div className="course-progress-percentage text-right text-muted-small my-1">
                {progressPercentage}%
              </div>

              <div className="course-progress-bar mb-1">
                <div
                  className="course-progress-bar-fill"
                  style={{
                    width: `${progressPercentage}%`,
                  }}
                />
              </div>

              <div className="course-progress-lessons text-right text-muted-small my-1">
                {completedLessons}/{totalLessons} lessons
              </div>
            </div>
          );
        }

        return "-";
      },
    },

    // {
    //   Header: "Actions",
    //   disableSortBy: true,
    //   Cell: ({ row }) => {
    //     const isRowExpanded = expandedRowId === row.original.id;

    //     return (
    //       <button
    //         className="btn btn-link p-0 btn-sm"
    //         onClick={() => handleRowClick(row.original.id)}
    //       >
    //         {isRowExpanded ? "View Less" : "View More"}
    //       </button>
    //     );
    //   },
    // },
  ];

  const sessionColumns = [
    {
      Header: "Sr. No.",
      accessor: "id",
    },
    // {
    //   Header: "Batch",
    //   accessor: "sessionName",
    // },
    {
      Header: "Scheduled Date",
      accessor: "date",
    },
    {
      Header: "Start Time",
      accessor: "startTime",
    },
    {
      Header: "End Time",
      accessor: "endTime",
    },
    { Header: "Trainee", accessor: "trainerName" },
    {
      Header: "Attendance",
      accessor: "attendance",
      Cell: ({ value }) => (
        <Badge
          color={
            value === "PRESENT"
              ? "success"
              : value === "ABSENT"
                ? "warning"
                : "secondary"
          }
          pill
        >
          {value}
        </Badge>
      ),
    },
  ];

  // const assessmentColumns = [
  //   {
  //     Header: "Sr. No.",
  //     accessor: "id",
  //   },
  //   {
  //     Header: "Assessment Name",
  //     accessor: "assessmentName",
  //   },
  //   {
  //     Header: "Attempt Count",
  //     accessor: "noOfAttempts",
  //   },
  //   {
  //     Header: "Score",
  //     accessor: "score",
  //   },
  //   {
  //     Header: "Result",
  //     accessor: "result",
  //     Cell: ({ value }) => (
  //       <Badge
  //         color={
  //           value === "PASS"
  //             ? "success"
  //             : value === "FAIL"
  //               ? "warning"
  //               : "secondary"
  //         }
  //         pill
  //       >
  //         {value}
  //       </Badge>
  //     ),
  //   },
  //   {
  //     Header: "Duration",
  //     accessor: "assessmentDuration",
  //   },
  //   {
  //     Header: "Assessment Date",
  //     accessor: "assessmentDate",
  //   },
  // ];

  // const coursesList = [
  //   {
  //     id: 1,
  //     courseName: "Electrical Safety",
  //     category: "Safety",
  //     level: "Beginner",
  //     type: "Recorded",
  //     contentDeliveryMode: "Online",
  //     enrolledOn: "15 Jan 2025, 05:13 PM",
  //     status: "COMPLETED",
  //   },
  //   {
  //     id: 2,
  //     courseName: "Power Systems Advanced",
  //     category: "Power Systems",
  //     level: "Advanced",
  //     type: "Live",
  //     contentDeliveryMode: "Online",
  //     enrolledOn: "18 Jan 2025, 05:13 PM",
  //     status: "IN_PROGRESS",
  //   },
  //   {
  //     id: 3,
  //     courseName: "Industrial Wiring Basics",
  //     category: "Electrical",
  //     level: "Beginner",
  //     type: "Recorded",
  //     contentDeliveryMode: "Offline + Online",
  //     enrolledOn: "20 Jan 2025, 05:13 PM",
  //     status: "COMPLETED",
  //   },
  //   {
  //     id: 4,
  //     courseName: "Substation Operations",
  //     category: "Power Systems",
  //     level: "Intermediate",
  //     type: "Live",
  //     contentDeliveryMode: "Offline",
  //     enrolledOn: "22 Jan 2025, 05:13 PM",
  //     status: "COMPLETED",
  //   },
  //   {
  //     id: 5,
  //     courseName: "Transformer Maintenance",
  //     category: "Electrical",
  //     level: "Advanced",
  //     type: "Recorded",
  //     contentDeliveryMode: "Online",
  //     enrolledOn: "24 Jan 2025, 05:13 PM",
  //     status: "IN_PROGRESS",
  //   },
  //   {
  //     id: 6,
  //     courseName: "Energy Management Basics",
  //     category: "Energy",
  //     level: "Beginner",
  //     type: "Recorded",
  //     contentDeliveryMode: "Online",
  //     enrolledOn: "26 Jan 2025, 05:13 PM",
  //     status: "COMPLETED",
  //   },
  //   {
  //     id: 7,
  //     courseName: "Panel Wiring Techniques",
  //     category: "Electrical",
  //     level: "Intermediate",
  //     type: "Live",
  //     contentDeliveryMode: "Offline + Online",
  //     enrolledOn: "27 Jan 2025, 05:13 PM",
  //     status: "IN_PROGRESS",
  //   },
  //   {
  //     id: 8,
  //     courseName: "Advanced Electrical Safety",
  //     category: "Safety",
  //     level: "Advanced",
  //     type: "Recorded",
  //     contentDeliveryMode: "Online",
  //     enrolledOn: "28 Jan 2025, 05:13 PM",
  //     status: "COMPLETED",
  //   },
  //   {
  //     id: 9,
  //     courseName: "Grid Operations Fundamentals",
  //     category: "Power Systems",
  //     level: "Beginner",
  //     type: "Live",
  //     contentDeliveryMode: "Offline",
  //     enrolledOn: "29 Jan 2025, 05:13 PM",
  //     status: "COMPLETED",
  //   },
  // ];

  const courseScheduleList = [
    {
      id: 1,
      sessionName: "Introduction to Electrical Safety",
      date: "20 Jan 2025",
      startTime: "10:00 AM",
      endTime: "11:00 AM",
      attendance: "PRESENT",
      trainerName: "Sheetal Sharma",
    },
    {
      id: 2,
      sessionName: "Electrical Hazards & Risks",
      date: "22 Jan 2025",
      startTime: "10:00 AM",
      endTime: "11:30 AM",
      attendance: "PRESENT",
      trainerName: "Gokul Patil",
    },
    {
      id: 3,
      sessionName: "Safe Handling of Electrical Equipment",
      date: "24 Jan 2025",
      startTime: "10:00 AM",
      endTime: "12:00 PM",
      attendance: "ABSENT",
      trainerName: "Rahul Verma",
    },
    {
      id: 4,
      sessionName: "Emergency Procedures & First Aid",
      date: "26 Jan 2025",
      startTime: "10:00 AM",
      endTime: "11:00 AM",
      attendance: "Not Marked",
      trainerName: "Sheetal Sharma",
    },
  ];


  const getDoughnutConfig = (key, labels, data, colorsArr) => ({
    data: {
      labels,
      datasets: [
        {
          data,
          backgroundColor: colorsArr,
          borderWidth: 0,
        },
      ],
    },
    options: {
      responsive: true,
      cutout: "80%",
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              // const label = context.label || "";
              const value = context.parsed;

              return ` ${value} ${key === "attendance" ? "days" : "%"} `;
            },
          },
        },
      },
      animation: {
        animateScale: false,
        animateRotate: false,
      },
    },
  });

  // const centerTextPlugin = {
  //   id: "centerText",
  //   beforeDraw(chart) {
  //     const { ctx, width, height } = chart;
  //     const centerText = chart.options.centerText;

  //     if (!centerText) return;

  //     ctx.save();

  //     ctx.font = "600 12px Open Sans";
  //     ctx.fillStyle = colors.gray[600];
  //     ctx.textAlign = "center";
  //     ctx.textBaseline = "middle";
  //     ctx.fillText(centerText.label, width / 2, height / 2 - 10);

  //     ctx.font = "bold 18px Open Sans";
  //     ctx.fillStyle = centerText.color;
  //     ctx.fillText(centerText.value, width / 2, height / 2 + 10);

  //     ctx.restore();
  //   },
  // };

  const attendancePresent = 85;
  const attendanceAbsent = 10;
  const attendanceNotMarked = 5;

  const attendanceChart = getDoughnutConfig(
    "attendance",
    ["Present", "Absent", "Not Marked"],
    [attendancePresent, attendanceAbsent, attendanceNotMarked],
    [colors.theme.success, colors.theme.warning, colors.gray[400]],
  );

  attendanceChart.options.centerText = {
    label: "Attendance",
    value: `${attendancePresent}%`,
    color: colors.theme.success,
  };

  const completionCompleted = 95;
  const completionInProgress = 5;
  const completionPending = 0;

  const completionChart = getDoughnutConfig(
    "status",
    [("Completed", "In Progress", "Pending")],
    [completionCompleted, completionInProgress, completionPending],
    [colors.theme.success, colors.gray[400], colors.theme.warning],
  );

  completionChart.options.centerText = {
    label: "Course Progress",
    value: `${completionCompleted}%`,
    color: colors.theme.success,
  };

  const renderCol = (label, value) => {
    return (
      <Col md="6" className="mb-3">
        <h4 className="text-muted mb-0">{label}</h4>
        <h4 className="mb-0">{value || "-"}</h4>
      </Col>
    );
  };

  const ExpandedCourseDetails = ({ course }) => {
    return (
      <div className="expanded-container">
        <Row>
          <Col md="12">
            <div className="course-summary">
              <div className="mb-3">
                <h4 className="text-muted mb-0">Course Name</h4>
                <h3 className="text-deep-blue mb-0">{course.courseName}</h3>
              </div>

              <Row>
                {renderCol("Type", course.type)}
                {renderCol("Level", course.level)}
                {renderCol("Mode", course.contentDeliveryMode)}
                {renderCol("Enrolled On", course.enrolledOn)}
              </Row>
            </div>
          </Col>
          {/* <Col md="6">
            <div className="progress-charts">
              <div className="text-center">
                <Doughnut
                  data={completionChart.data}
                  options={completionChart.options}
                  plugins={[centerTextPlugin]}
                  className="chart-canvas"
                />
              </div>
              <div className="text-center">
                <Doughnut
                  data={attendanceChart.data}
                  options={attendanceChart.options}
                  plugins={[centerTextPlugin]}
                  className="chart-canvas"
                />
              </div>
            </div>
          </Col> */}
        </Row>

        <div className="divider"></div>

        <div className="mt-3">
          <h4 className="mb-1">Course Schedule & Attendance</h4>
          <h5 className="text-muted mb-3">
            Showing batch-wise schedule and trainee attendance
          </h5>
          <CommonTable
            columns={sessionColumns}
            data={courseScheduleList || []}
            defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
            activePage={pageNum}
            setPageNum={setPageNum}
            totalPages={Math.ceil(
              courseScheduleList.length / DEFAULT_RECORDS_PER_PAGE,
            )}
            showPaginationBottom={true}
          />
        </div>

        {/* <div className="divider"></div>

        <div className="mt-3">
          <h4 className="mb-1">Assessments</h4>
          <h5 className="text-muted mb-3">Showing assessment performance</h5>
          <CommonTable
            columns={assessmentColumns}
            data={assessmentList || []}
            defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
            activePage={pageNum}
            setPageNum={setPageNum}
            totalPages={Math.ceil(
              assessmentList.length / DEFAULT_RECORDS_PER_PAGE,
            )}
            showPaginationBottom={true}
          />
        </div> */}
      </div>
    );
  };

  // const handleRowClick = (id) => {
  //   setExpandedRowId((prev) => (prev === id ? null : id));
  // };

  return (
    <Card className="mb-6">
      {/*<CardHeader>
         <h4 className="mb-0">Enrolled Courses</h4> 
      </CardHeader>*/}
      <CardBody className="p-4">
        <div className="d-flex align-items-center justify-content-between mb-5 flex-wrap flex-column flex-md-row">
          <div className="d-flex flex-wrap align-items-center filter-group">
            <Input
              placeholder="Search by course name"
              className="mr-2 mb-2 filter-input-large"
              value={filterState.searchName}
              onChange={(e) =>
                setFilterState({ ...filterState, searchName: e.target.value })
              }
              onKeyDown={handleKeyDown}
            />

            <Select
              className="mr-2 mb-2 filter-select"
              options={courseCategoryOptions}
              placeholder="Select category"
              classNamePrefix="react-select"
              styles={fixedWidthCustomStyle()}
              value={filterState.searchCategory}
              onChange={(selected) =>
                setFilterState({ ...filterState, searchCategory: selected })
              }
            />

            <Select
              className="mr-2 mb-2 filter-select"
              options={COURSE_LEVELS}
              placeholder="Select level"
              classNamePrefix="react-select"
              styles={fixedWidthCustomStyle()}
              value={filterState.searchLevel}
              onChange={(selected) =>
                setFilterState({ ...filterState, searchLevel: selected })
              }
            />

            <Select
              className="mr-2 mb-2 filter-select"
              options={COURSE_TYPES}
              placeholder="Select type"
              classNamePrefix="react-select"
              styles={fixedWidthCustomStyle()}
              value={filterState.searchType}
              onChange={(selected) =>
                setFilterState({ ...filterState, searchType: selected })
              }
            />

            <Select
              className="mr-2 mb-2 filter-select"
              options={COURSE_MODE}
              placeholder="Select mode"
              classNamePrefix="react-select"
              styles={fixedWidthCustomStyle()}
              value={filterState.searchMode}
              onChange={(selected) =>
                setFilterState({ ...filterState, searchMode: selected })
              }
            />

            <Select
              className="mr-2 mb-2 filter-select"
              options={BATCH_STATUS_OPTIONS}
              placeholder="Select status"
              classNamePrefix="react-select"
              styles={fixedWidthCustomStyle()}
              components={{ Option, SingleValue }}
              value={filterState.searchStatus}
              onChange={(selected) =>
                setFilterState((prev) => ({
                  ...prev,
                  searchStatus: selected,
                }))
              }
            />

            {/* <DatePicker
              selected={filterState.searchDate[0]}
              onChange={(dates) =>
                setFilterState((prev) => ({
                  ...prev,
                  searchDate: dates,
                }))
              }
              startDate={filterState.searchDate[0]}
              endDate={filterState.searchDate[1]}
              selectsRange
              isClearable={!!filterState.searchDate[0]}
              placeholderText="Select enrolled on date or range"
              className="form-control mr-2 mb-2 filter-input-large"
              dateFormat="dd-MMM-yyyy"
              showMonthDropdown
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={100}
              calendarClassName="custom-calendar"
            /> */}

            <Button
              color="default"
              className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${listState.showLoaderOnClick ? "search-btn" : ""}`}
              onClick={handleSearch}
            >
              {listState.showLoaderOnClick ? (
                <i className="fa fa-spinner fa-spin loader" />
              ) : (
                <>
                  <i className="fa fa-search mr-1" />
                  Search
                </>
              )}
            </Button>
            {filterState.filterParams.length > 0 && (
              <Button
                color="default"
                className="mb-2 filter-btn btn-simple btn-deep-blue"
                onClick={clearSearch}
              >
                <i className="fa fa-times mr-1" /> Clear
              </Button>
            )}
          </div>
        </div>
        <div className="mt-2 mb-3 mx-1">
          {traineeCoursesData?.data?.totalRecords > 0 ? (
            <>
              {filterState.filterParams.length > 0 ? (
                <span className="total-records">
                  Showing {traineeCoursesData?.data?.totalFilteredRecords} of{" "}
                  {traineeCoursesData?.data?.totalRecords} Records
                </span>
              ) : (
                <span className="total-records">
                  Total Records : {traineeCoursesData?.data?.totalRecords}
                </span>
              )}
            </>
          ) : null}
        </div>

        {listState.isLoading ? (
          <div className="loaderContainer-sm ">
            <i className="fa fa-spinner fa-spin fa-2xl loader" />
          </div>
        ) : listState.error ? (
          <div className="loaderContainer-sm  error-style">
            <div className="error-icon">
              <i className="fa-solid fa-circle-exclamation"></i>
            </div>
            <span>{listState.error}</span>
          </div>
        ) : listState.noRecords ? (
          <div className="loaderContainer-sm empty-style">
            <div className="empty-icon">
              <i className="fa-solid fa-circle-info"></i>
            </div>
            <span>No enrolled courses found.</span>
          </div>
        ) : (
          <div className="table-responsive">
            <CommonTable
              columns={columns}
              data={tableData || []}
              defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
              activePage={pageNum}
              setPageNum={setPageNum}
              totalPages={Math.ceil(
                (traineeCoursesData?.data?.totalFilteredRecords || 0) /
                  (traineeCoursesData?.data?.noOfRecordsPerPage ||
                    DEFAULT_RECORDS_PER_PAGE),
              )}
              showPaginationBottom={true}
              // rowClick={(row) => handleOnRowClick(row)}
              expandedRowIndex={expandedRowId}
              renderExpandedRow={(row) =>
                expandedRowId === row.id ? (
                  <ExpandedCourseDetails course={row} />
                ) : null
              }
            />
          </div>
        )}
      </CardBody>
      <AttendanceRightPanel
        isOpen={isRightPanelOpen}
        onClose={() => {
          setIsRightPanelOpen(false);
          setSelectedCourse(null);
        }}
        selectedTrainee={{
          traineeId: traineeID,
        }}
        courseID={
          selectedCourse?.courseID
            ? urlEncodedId(selectedCourse.courseID)
            : null
        }
        batchId={
          selectedCourse?.batch?.batchID
            ? urlEncodedId(selectedCourse.batch.batchID)
            : null
        }
        courseName={selectedCourse?.courseName}
        batchName={selectedCourse?.batch?.batchName}
      />
    </Card>
  );
}

export default TraineeCourses;
